/**
 * SuggestionsCard — "¿qué fabrico?" en /industry.
 *
 * Top N productos que el char fabricó en los últimos 30 días, rankeados por
 * (margin_per_hour × materials_coverage_pct). Backend ya hace el ranking;
 * este componente es sólo display.
 *
 *  - Cada row: BP + product name, last manufactured, margin/h (gold),
 *    coverage bar visual, recommendation tag color-coded.
 *  - "ready_to_manufacture" (verde): tenés todos los materials en hangar.
 *  - "mostly_ready" (cyan): ≥50% de materials en hangar.
 *  - "needs_materials" (gris): <50% — vas a tener que comprar.
 *  - "no_margin_data" (mute): sin margen computable (Fuzzwork down, etc).
 *  - Empty state si no hay historial de manufactura.
 *
 * Caveat: no chequea skills. Asumimos que el user ya sabe qué BP puede
 * correr (es industrialist — conoce su set).
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISKShort } from '../api'
import { LoadingSkeleton } from './LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

type Recommendation =
  | 'ready_to_manufacture'
  | 'mostly_ready'
  | 'needs_materials'
  | 'no_margin_data'

type Suggestion = {
  product_type_id: number
  product_name: string | null
  blueprint_name: string | null
  last_manufactured: string | null
  manufacture_count_30d: number
  current_margin_per_run: number | null
  current_margin_per_hour: number | null
  current_margin_pct: number | null
  materials_coverage_pct: number
  materials_needed: number
  materials_in_hangar: number
  score: number | null
  recommendation: Recommendation
}

const REC_STYLE: Record<
  Recommendation,
  { label: string; cls: string; dotCls: string }
> = {
  ready_to_manufacture: {
    label: 'Listo para fabricar',
    cls: 'bg-eve-success/10 text-eve-success border-eve-success/30',
    dotCls: 'bg-eve-success',
  },
  mostly_ready: {
    label: 'Casi listo',
    cls: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    dotCls: 'bg-cyan-400',
  },
  needs_materials: {
    label: 'Necesita materials',
    cls: 'bg-eve-panel text-eve-muted border-eve-border',
    dotCls: 'bg-eve-muted',
  },
  no_margin_data: {
    label: 'Sin datos de margin',
    cls: 'bg-eve-warn/10 text-eve-warn border-eve-warn/30',
    dotCls: 'bg-eve-warn',
  },
}

export function SuggestionsCard() {
  const [limit, setLimit] = useState<5 | 10 | 20>(5)
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['industry-suggestions', limit, forceRefetchKey],
    queryFn: () => api.industrySuggestions(limit),
  })

  const suggestions: Suggestion[] = data?.suggestions ?? []
  const reason = data?.reason

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Qué fabricar</h2>
          <p className="text-xs text-eve-muted">
            Top N por{' '}
            <span className="font-mono">margin/h × materials_coverage</span>.
            Solo productos que fabricaste en los últimos 30 días.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LimitToggle value={limit} onChange={setLimit} disabled={isFetching} />
          <button
            onClick={bump}
            disabled={isFetching}
            className="text-xs px-3 py-1.5 rounded-md border bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text hover:border-eve-accent/40 transition-colors disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <LoadingSkeleton variant="rows" rows={4} />
      ) : reason === 'no_manufacturing_history' ? (
        <EmptyState message={data?.message ?? ''} />
      ) : suggestions.length === 0 ? (
        <div className="text-eve-muted italic py-8 text-center text-sm">
          Sin suggestions todavía.
        </div>
      ) : (
        <div className="space-y-2">
          {suggestions.map((s) => (
            <SuggestionRow key={s.product_type_id} s={s} />
          ))}
        </div>
      )}
    </div>
  )
}

function LimitToggle({
  value,
  onChange,
  disabled,
}: {
  value: 5 | 10 | 20
  onChange: (v: 5 | 10 | 20) => void
  disabled?: boolean
}) {
  const opts: Array<5 | 10 | 20> = [5, 10, 20]
  return (
    <div className="flex border border-eve-border rounded-md overflow-hidden">
      {opts.map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          disabled={disabled}
          className={`text-xs px-2.5 py-1.5 transition-colors ${
            value === n
              ? 'bg-eve-accent/10 text-eve-accent'
              : 'bg-eve-panel text-eve-muted hover:text-eve-text'
          } ${disabled ? 'opacity-50' : ''}`}
        >
          Top {n}
        </button>
      ))}
    </div>
  )
}

function SuggestionRow({ s }: { s: Suggestion }) {
  const rec = REC_STYLE[s.recommendation]
  const marginPositive = (s.current_margin_per_hour ?? 0) > 0

  return (
    <details className="border border-eve-border rounded-md bg-eve-bg/40 group">
      <summary className="cursor-pointer p-3 hover:bg-eve-panel/40 transition-colors list-none">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium truncate">
                {s.blueprint_name ?? s.product_name ?? `Product #${s.product_type_id}`}
              </span>
              {s.product_name && s.blueprint_name !== s.product_name && (
                <span className="text-xs text-eve-muted font-mono">
                  → {s.product_name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 text-[11px] text-eve-muted flex-wrap">
              <span className="font-mono">
                {s.manufacture_count_30d}× en 30d
              </span>
              {s.last_manufactured && (
                <span>· último {formatDate(s.last_manufactured)}</span>
              )}
              <span className={`px-1.5 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider ${rec.cls}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${rec.dotCls} mr-1 align-middle`} />
                {rec.label}
              </span>
            </div>
          </div>

          <div className="text-right shrink-0 min-w-[160px]">
            {s.current_margin_per_hour !== null ? (
              <>
                <div
                  className={`text-sm font-mono font-semibold ${
                    marginPositive ? 'text-eve-accent' : 'text-eve-danger'
                  }`}
                  title="Margin por hora, basado en el estado actual del mercado"
                >
                  {formatISKShort(s.current_margin_per_hour)} ISK/h
                </div>
                {s.current_margin_pct !== null && (
                  <div className="text-[10px] text-eve-muted font-mono">
                    {marginPositive ? '+' : ''}
                    {s.current_margin_pct.toFixed(1)}% margin
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-eve-muted italic">—</div>
            )}
          </div>
        </div>

        <CoverageBar pct={s.materials_coverage_pct} />
      </summary>

      <div className="border-t border-eve-border px-3 py-2 text-xs bg-eve-panel/20 space-y-1">
        <div className="flex items-center gap-4 flex-wrap text-eve-muted">
          <span>
            Materials: <span className="text-eve-text font-mono">{s.materials_in_hangar}/{s.materials_needed}</span> en hangar
          </span>
          {s.current_margin_per_run !== null && (
            <span>
              Margin/run:{' '}
              <span className="text-eve-text font-mono">
                {formatISKShort(s.current_margin_per_run)} ISK
              </span>
            </span>
          )}
          {s.score !== null && (
            <span>
              Score: <span className="text-eve-text font-mono">{Math.round(s.score).toLocaleString('en-US')}</span>
            </span>
          )}
        </div>
      </div>
    </details>
  )
}

function CoverageBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct))
  // Color según coverage
  const color =
    clamped >= 100
      ? 'bg-eve-success'
      : clamped >= 50
        ? 'bg-cyan-400'
        : 'bg-eve-muted'
  return (
    <div className="mt-2 h-1 bg-eve-panel rounded overflow-hidden" title={`Materials coverage: ${clamped}%`}>
      <div
        className={`h-full transition-all ${color}`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-8 space-y-1">
      <div className="text-eve-muted text-sm italic">
        {message || 'Sin historial de manufactura en los últimos 30 días.'}
      </div>
      <div className="text-eve-muted text-xs">
        Empezá fabricando algo y volvé en 30 días para ver suggestions.
      </div>
    </div>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const now = Date.now()
  const diffMs = now - d.getTime()
  const days = Math.floor(diffMs / 86_400_000)
  if (days === 0) return 'hoy'
  if (days === 1) return 'ayer'
  if (days < 7) return `hace ${days}d`
  if (days < 30) return `hace ${Math.floor(days / 7)}sem`
  return d.toLocaleDateString()
}
